import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import NotFound from '@/shared/ui/NotFound';
import SpinnerThreeDots from '@/shared/ui/SpinnerThreeDots';
import App from './App';
import RequireSession from './RequireSession';
import RouteError from './RouteError';
import SsoLayout from './SsoLayout';
import WatchlistLayout from './WatchlistLayout';
import { searchSerialization } from './searchSerialization';

// The query keys a route reads, each kept as the string the URL carried;
// the pages that pass the issuer's own parameters through read the raw
// query instead.
function strings<const K extends string>(...keys: K[]) {
  return (
    raw: Partial<Record<K, string>> & SearchSchemaInput,
  ): Partial<Record<K, string>> => {
    const out: Partial<Record<K, string>> = {};
    for (const key of keys) {
      const value = raw[key];
      if (typeof value === 'string') out[key] = value;
    }
    return out;
  };
}

const rootRoute = createRootRoute({
  component: App,
  notFoundComponent: NotFound,
});

// The dashboard: signed in, outside the card.
export const homeGateRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'home',
  component: RequireSession,
});

export const dashboardRoute = createRoute({
  getParentRoute: () => homeGateRoute,
  path: '/',
  validateSearch: strings('authuser', 'linked'),
  component: lazyRouteComponent(() => import('./pages/Dashboard')),
});

// The 420px card every SSO screen but the dashboard renders in.
const cardRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'card',
  component: SsoLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => cardRoute,
  path: 'login',
  validateSearch: strings('returnTo', 'error'),
  component: lazyRouteComponent(() => import('./pages/Login')),
});

export const sessionMismatchRoute = createRoute({
  getParentRoute: () => cardRoute,
  path: 'session-mismatch',
  validateSearch: strings('expected_session', 'return_to'),
  component: lazyRouteComponent(() => import('./pages/SessionMismatch')),
});

const selectAccountRoute = createRoute({
  getParentRoute: () => cardRoute,
  path: 'select-account',
  component: lazyRouteComponent(() => import('./pages/SelectAccount')),
});

const consentRoute = createRoute({
  getParentRoute: () => cardRoute,
  path: 'consent',
  component: lazyRouteComponent(() => import('./pages/Consent')),
});

export const magicLinkConfirmRoute = createRoute({
  getParentRoute: () => cardRoute,
  path: 'magic-link-confirm',
  validateSearch: strings('token', 'returnTo'),
  component: lazyRouteComponent(() => import('./pages/MagicLinkConfirm')),
});

// The setup page is where an unfinished profile is sent — it is the one
// signed-in route that must not bounce there itself.
export const setupGateRoute = createRoute({
  getParentRoute: () => cardRoute,
  id: 'setup',
  component: SetupGate,
});

export const profileSetupRoute = createRoute({
  getParentRoute: () => setupGateRoute,
  path: 'profile/setup',
  validateSearch: strings('returnTo', 'session_id', 'authuser'),
  component: lazyRouteComponent(() =>
    import('./pages/Profile').then((page) => ({ default: page.ProfileSetup })),
  ),
});

export const accountGateRoute = createRoute({
  getParentRoute: () => cardRoute,
  id: 'account',
  component: RequireSession,
});

export const profileEditRoute = createRoute({
  getParentRoute: () => accountGateRoute,
  path: 'profile/edit',
  validateSearch: strings('returnTo', 'session_id', 'authuser'),
  component: lazyRouteComponent(() =>
    import('./pages/Profile').then((page) => ({ default: page.ProfileEdit })),
  ),
});

// Its own wider card, no brand row: the stand's welcome screen.
export const demoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'demo',
  validateSearch: strings('secret'),
  component: lazyRouteComponent(() => import('./pages/Demo')),
});

const demoGuestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'demo/scorearena',
  component: lazyRouteComponent(() => import('./pages/DemoGuest')),
});

const mockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'mock',
  component: WatchlistLayout,
});

const tokensRoute = createRoute({
  getParentRoute: () => mockRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/Tokens')),
});

export const tokenRoute = createRoute({
  getParentRoute: () => mockRoute,
  path: '$id',
  component: lazyRouteComponent(() => import('./pages/Token')),
});

export const routeTree = rootRoute.addChildren([
  homeGateRoute.addChildren([dashboardRoute]),
  cardRoute.addChildren([
    loginRoute,
    sessionMismatchRoute,
    selectAccountRoute,
    consentRoute,
    magicLinkConfirmRoute,
    setupGateRoute.addChildren([profileSetupRoute]),
    accountGateRoute.addChildren([profileEditRoute]),
  ]),
  demoRoute,
  demoGuestRoute,
  mockRoute.addChildren([tokensRoute, tokenRoute]),
]);

// Every page is its own chunk: a link fetches its target on hover, and a
// chunk still loading after the router's default second shows the spinner.
export const router = createRouter({
  routeTree,
  ...searchSerialization,
  defaultPreload: 'intent',
  defaultPendingComponent: PagePending,
  defaultErrorComponent: RouteError,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function SetupGate() {
  return <RequireSession requireSetup={false} />;
}

function PagePending() {
  return (
    <div className="mt-xl flex justify-center">
      <SpinnerThreeDots color="var(--gray)" size={10} />
    </div>
  );
}
